import React from 'react'

import { Accordion, Colors, Subsection, useModalContext } from '@fourthwall/components'
import { EmptyProductsApprovalStatus } from '@modules/Dashboard/pages/Apps/modules/EmptyProductsApprovalStatus'

import type { ProductsSyncStatus } from '../../AppsTiktokShop.types'
import { productSyncStatuses } from '../../consts'
import { getSyncedProductsHeading } from '../../utils/getSyncedProductsHeading'
import { ErrorBanner } from '../ErrorBanner/ErrorBanner'
import { ProductsSyncStatusHeading } from '../ProductsSyncStatusHeading/ProductsSyncStatusHeading'
import { ProductsSyncStatusModal } from '../ProductsSyncStatusModal/ProductsSyncStatusModal'

type SyncProduct = {
  id: string
  imageSrc?: string
  name: string
  issues: {
    description: string
    url?: string
  }[]
}

export type SyncProductsProps = {
  products?: {
    active: SyncProduct[]
    pending: SyncProduct[]
    denied: SyncProduct[]
  }
  warnings: {
    synchronizationLimitReached: boolean
  }
}

export const SyncProducts = ({ products, warnings }: SyncProductsProps) => {
  const { open } = useModalContext()

  const handleSeeDetails = (productSyncStatus: ProductsSyncStatus) => {
    if (!products) return

    open(ProductsSyncStatusModal, {
      title: getSyncedProductsHeading(productSyncStatus, products[productSyncStatus].length),
      products: products[productSyncStatus],
    })
  }

  const renderSyncedProductsList = () => {
    if (!products) return null

    if (productSyncStatuses.every((productSyncStatus) => !products[productSyncStatus].length)) {
      return <div style={{ marginTop: 16, color: Colors.GS600 }}>No products connected yet</div>
    }

    return (
      <div style={{ marginTop: 16 }}>
        <Accordion>
          {productSyncStatuses.map((productSyncStatus) => (
            <Accordion.Item
              heading={
                <ProductsSyncStatusHeading
                  status={productSyncStatus}
                  label={getSyncedProductsHeading(
                    productSyncStatus,
                    products[productSyncStatus].length,
                  )}
                  onSeeDetails={
                    products[productSyncStatus].length
                      ? () => handleSeeDetails(productSyncStatus)
                      : undefined
                  }
                />
              }
            />
          ))}
        </Accordion>
      </div>
    )
  }

  if (!products) {
    return <EmptyProductsApprovalStatus appName="TikTok" />
  }

  return (
    <Subsection title="Product sync status">
      <ErrorBanner enabled={warnings.synchronizationLimitReached} appearance="warning">
        You have reached maximum number of product to be synced for today, we will resume the update
        automatically tomorrow.
      </ErrorBanner>
      <div>
        Products that meet TikTok’s requirements are automatically synced to your TikTok Shop.
      </div>
      {renderSyncedProductsList()}
    </Subsection>
  )
}
